/**
 * What an answer is, shared by every answer control.
 *
 * Its own file rather than living with the evaluator: the controls are being built first and
 * must not depend on the flow engine, which is a later step and will change.
 */

/** A multi-select holds several option values; everything else holds one value. */
export type AnswerValue = string | number | string[];

export type Answers = Record<string, AnswerValue | undefined>;

/** Present and non-empty. An empty multi-select array is NOT an answer. */
export function isAnswered(v: AnswerValue | undefined): boolean {
  if (v === undefined || v === '') return false;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

/**
 * A check Next has to pass before the walk advances.
 *
 * Returns true to allow it. The control shows its OWN message when it refuses — the page
 * only needs to know whether to move on, and an error about an account id belongs next to
 * the account field, not in a banner at the top.
 */
export type AnswerGate = () => Promise<boolean>;

/** Props every answer control takes, so `AnswerBody` can dispatch on kind alone. */
export interface AnswerControlProps {
  value: AnswerValue | undefined;
  onChange: (next: AnswerValue | undefined) => void;
  /** An answered card that has been reopened is still editable; a submitted one is not. */
  readOnly?: boolean;
  /**
   * Register an async check that Next must pass; `null` removes it.
   *
   * Only one control needs this today (SFDC), and it needs it because "is this a real
   * account" cannot be answered from the typed value alone — it takes a round trip. The
   * alternative, a separate 校验 button the BD must remember to press, was what this code did
   * first and it meant an unverified id could walk the whole questionnaire: `nextDisabled`
   * only looked at whether the field was non-empty. The live AgentHub validates inside its
   * Next handler and refuses to advance, so this is the shape that matches it.
   *
   * Optional, and absent for every other kind — a select has nothing to round-trip.
   */
  registerGate?: (gate: AnswerGate | null) => void;
}
